import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './App.css';
import { Box, CircularProgress, Typography } from '@mui/material';

function BillListPage() {
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const { username } = useParams(); // Extract username from the URL

  const icons = {};
  icons['Strongly Disagree'] = {'emoji': '🤯', 'color': '#F99E17'};
  icons['Disagree'] =  {'emoji': '😕', 'color': '#FFE500'};
  icons['Neutral'] =  {'emoji': '😐', 'color': '#949494'};
  icons['Agree'] =  {'emoji': '🙂', 'color': '#00E709'};
  icons['Strongly Agree'] =  {'emoji': '🥳', 'color': '#00A006'};

  useEffect(() => {
    const fetchBills = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bills/public-list/${username}/`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bills');
        }

        const data = await response.json();
        setBills(data.bills || []);
      } catch (error) {
        console.error('Error fetching bills:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBills();
  }, [username]);

  return (
    <Box sx={{ padding: 3 }}>
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" mt={6}>
          <CircularProgress size={50} />
        </Box>
      ) : (
        <>
          {bills.length === 0 ? (
            <Typography>No bills found.</Typography>
          ) : (
            bills.map((bill) => (
              <a
                key={bill.id}
                href={`https://analytics.civiclyenvolved.com/bill/${bill.state}/${bill.year_introduced}/${bill.bill_number}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Box
                  sx={{
                    backgroundColor: '#F4F4F4',
                    borderRadius: 2,
                    padding: 2,
                    maxWidth: '50%',
                    marginBottom: 2,
                    fontFamily: 'Lato',
                  }}
                >
                  <Typography>
                    {`${bill.year_introduced} | ${bill.state} ${bill.bill_number} ${bill.title ? ` - ${bill.title}` : ''}`}
                  </Typography>
                  {bill.policy_objective ? (
                    <Typography sx={{ marginTop: 1 }}>
                      {bill.policy_objective}
                    </Typography>
                  ) : bill.description && (
                    <Typography sx={{ marginTop: 1 }}>
                      {bill.description.slice(0, 45)}...
                    </Typography>
                  )}

                  {bill.vote_info?.vote_type && (
                    <Typography sx={{ marginTop: 1 }}>
                      <b>Our Position:</b> {icons[bill.vote_info.vote_type]?.emoji || bill.vote_info.vote_type}
                    </Typography>
                  )}

                  {bill.vote_info?.reason && (
                    <Typography sx={{ marginTop: 1 }}>
                        <b>Our Reason:</b> <span dangerouslySetInnerHTML={{ __html: bill.vote_info?.reason.replace(/\n/g, '<br />') }} />
                    </Typography>
                  )}

                  {bill.status && (
                    <Typography sx={{ marginTop: 1 }}>
                      <b>Status:</b> {bill.status}
                    </Typography>
                  )}
                  {bill.controversy_level && (
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 1 }}>
                      <Typography sx={{ marginRight: 1 }}>
                        <b>Controversy:</b>
                      </Typography>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <img
                          key={index}
                          src={index < Math.floor(bill.controversy_level / 2) ? '/images/fire-on.svg' : '/images/fire-off.svg'}
                          alt="Fire"
                          style={{ width: 20, height: 20, marginRight: 4 }}
                        />
                      ))}
                    </Box>
                  )}
                  {bill.sponsors && bill.sponsors.length > 0 && (
                    <Typography sx={{ marginTop: 1 }}>
                      <b>Sponsor(s):</b> {bill.sponsors.map((sponsor) => sponsor.name).join(', ')}
                    </Typography>
                  )}
                  {bill.category && (
                    <Typography sx={{ marginTop: 1 }}>
                      <b>AI Category:</b> {bill.category}
                    </Typography>
                  )}
                  {bill.labels && bill.labels.length > 0 && (
                    <Box sx={{ marginTop: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <b>Label(s):</b>
                      {bill.labels.map((label, index) => {
                        const isDark = index % 2 === 0;
                        const backgroundColor = isDark ? '#0B7269' : '#CCFBF1';
                        const textColor = isDark ? '#FFFFFF' : '#0B7269';

                        return (
                          <Box
                            key={index}
                            sx={{
                              backgroundColor,
                              color: textColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {label}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </a>
            ))
          )}
        </>
      )}
    </Box>
  );
}

export default BillListPage;